import { FC, ReactNode, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../../../context/searchContext";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { useSystemTypesApi } from "../../../../../http/patrol/useSystemTypes";
import FlexContent from "../../../../ui/FlexContent";
import Header, { HeaderPath } from "../../../../ui/Header";
import Tabs, { Tab } from "../../../../ui/Tabs";
import { PATROL } from "../../AdminSystemSettings";
import SystemTypeDetails from "./SystemTypeDetails";
import Shuttles from "./shuttles/Shuttles";

const SystemType: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { patrolSystemTypeId } = useParams<{ patrolSystemTypeId: string }>();
  const systemTypesApi = useSystemTypesApi();
  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("l_tab")) || 0;

  const aborter = useRef(new AbortController());
  const { error, data, trigger } = systemTypesApi.getAdminSystemType(aborter.current.signal);

  const tabs: Tab[] = [
    { title: i18n.translation.common.generalData, hasSubTabs: true },
    { title: i18n.translation.shuttles.plural },
  ];

  const contents: ReactNode[] = [
    <Tabs.SubTab>
      <FlexContent padding direction="row">
        <SystemTypeDetails systemType={!error ? data : undefined} disabled={!!error || !data} />
      </FlexContent>
    </Tabs.SubTab>,
    <Shuttles active={selectedIndex === 1} />,
  ];

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: PATROL },
    {
      url: `/admin/systems/${systemId}/settings?tab=4`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.systemTypes.plural}`,
    },
    { display: data?.name ?? "" },
  ];

  useEffect(() => {
    patrolSystemTypeId && trigger(patrolSystemTypeId);
  }, [patrolSystemTypeId]);

  return (
    <SearchContextProvider>
      <SystemTypeContent
        title={`${i18n.translation.systemTypes.singular}: ${data?.name ?? ""}`}
        error={error}
        path={path}
        tabs={tabs}
        contents={contents}
      />
    </SearchContextProvider>
  );
};

export default SystemType;

type SystemTypeContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  tabs: Tab[];
  contents: ReactNode[];
};

const SystemTypeContent: FC<SystemTypeContentProps> = ({ title, error, path, tabs, contents }) => {
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header title={tabTitle ? `${title} · ${tabTitle}` : title} error={error} path={path} buttons={actions} />
      <Tabs urlParameter="l_tab" tabs={tabs} contents={contents} />
    </>
  );
};
